import React from 'react';
import { useTenantTerm } from '../../hooks/useTenantTerm';
import { Archive, ArrowDown, ArrowUp, Car, Home, LucideIcon, Package, Store } from 'lucide-react';
import { Lease } from '../../types/leases';
import { LeaseObjectType } from '../../types/Enums';
import { formatCurrency } from '../../Helpers/FormatUtils';
import { coTenantEntries, leaseObjectLabel, leaseStatus, leaseStatusLabel, todayStockholm } from '../../Helpers/leaseUtils';
import { LeaseNames, LeaseSort, LeaseSortKey, RentPeriod, activeRentTotal, rentHeader, rentPer } from './leasesView';

const OBJECT_ICONS: Record<LeaseObjectType, LucideIcon> = {
    [LeaseObjectType.Apartment]: Home,
    [LeaseObjectType.CommercialUnit]: Store,
    [LeaseObjectType.ParkingSpot]: Car,
    [LeaseObjectType.StorageUnit]: Archive,
    [LeaseObjectType.Other]: Package,
};

interface Column {
    key: LeaseSortKey;
    label: string;
}

const COLUMNS: Column[] = [
    { key: 'building', label: 'Byggnad' },
    { key: 'object', label: 'Objekt' },
    { key: 'tenant', label: 'Hyresgäst' },
    { key: 'period', label: 'Period' },
    { key: 'amount', label: 'Hyra' },
    { key: 'status', label: 'Status' },
];

interface LeasesTableProps {
    leases: Lease[];
    names: LeaseNames;
    sort: LeaseSort;
    onSort: (key: LeaseSortKey) => void;
    /** Hidden while exactly one building is chosen. */
    showBuilding: boolean;
    onOpen: (lease: Lease) => void;
    /** Every rent is shown converted to this period, named in the Hyra header. */
    rentPeriod: RentPeriod;
}

/**
 * The leases of Hyresavtal as a sortable table; a row opens the lease. On
 * phones each row becomes a card (Small Screens "Tables → cards").
 */
const LeasesTable: React.FC<LeasesTableProps> = ({ leases, names, sort, onSort, showBuilding, onOpen, rentPeriod }) => {
    const rentLabel = rentHeader(rentPeriod);
    const tenantLabel = useTenantTerm().singular;
    const columns = (showBuilding ? COLUMNS : COLUMNS.filter(column => column.key !== 'building'))
        .map(column => (column.key === 'amount' ? { ...column, label: rentLabel } : column.key === 'tenant' ? { ...column, label: tenantLabel } : column));
    const today = todayStockholm();
    const total = activeRentTotal(leases, rentPeriod, today);

    return (
        <div className="std-box leases-table-box">
        <table className="leases-table">
            <thead>
                <tr>
                    {columns.map(column => {
                        const active = sort.key === column.key;
                        return (
                            <th
                                key={column.key}
                                className={`leases-table-col--${column.key}`}
                                aria-sort={active ? (sort.direction === 'asc' ? 'ascending' : 'descending') : 'none'}
                            >
                                <button type="button" className="leases-table-sort" onClick={() => onSort(column.key)}>
                                    {column.label}
                                    {active && (sort.direction === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />)}
                                </button>
                            </th>
                        );
                    })}
                </tr>
            </thead>
            <tbody>
                {leases.map(lease => {
                    const status = leaseStatus(lease, today);
                    const Icon = OBJECT_ICONS[lease.object.type] ?? Package;
                    const others = coTenantEntries(lease).length;
                    return (
                        <tr
                            key={lease.key}
                            id={`lease-${lease.key}`}
                            className={`leases-table-row ${status === 'ended' ? 'leases-table-row--ended' : ''}`}
                            tabIndex={0}
                            onClick={() => onOpen(lease)}
                            onKeyDown={e => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    onOpen(lease);
                                }
                            }}
                        >
                            {showBuilding && (
                                <td className="leases-table-col--building">{names.buildingName(lease.buildingKey)}</td>
                            )}
                            <td className="leases-table-col--object">
                                <span className="leases-table-object">
                                    <Icon size={16} aria-hidden="true" />
                                    <strong>{(names.objectLabel ?? leaseObjectLabel)(lease)}</strong>
                                </span>
                            </td>
                            <td className="leases-table-col--tenant">
                                {names.tenantName(lease.tenantKey)}
                                {others > 0 && <span className="text-muted"> +{others}</span>}
                            </td>
                            <td className="leases-table-col--period">
                                <span className="leases-table-label">Period </span>
                                {lease.startDate} – {lease.endDate ?? 'tills vidare'}
                            </td>
                            <td className="leases-table-col--amount">
                                <span className="leases-table-label leases-table-rent-label">{rentLabel}</span>
                                <span className="leases-table-amount">{formatCurrency(rentPer(lease, rentPeriod))}</span>
                            </td>
                            <td className="leases-table-col--status">
                                <span className={`lease-status lease-status--${status}`}>{leaseStatusLabel(status)}</span>
                            </td>
                        </tr>
                    );
                })}
            </tbody>
            <tfoot>
                <tr className="leases-table-total">
                    {/* Every column before Hyra; Status follows it. */}
                    <td colSpan={columns.length - 2} className="leases-table-total-label">
                        Summa, {total.activeCount} {total.activeCount === 1 ? 'aktivt avtal' : 'aktiva avtal'}
                    </td>
                    <td className="leases-table-col--amount">
                        <span className="leases-table-label leases-table-rent-label">{rentLabel}</span>
                        <span className="leases-table-amount">{formatCurrency(total.total)}</span>
                    </td>
                    <td />
                </tr>
            </tfoot>
        </table>
        </div>
    );
};

export default LeasesTable;
